import { useEffect, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import type { CookieConsentProps } from '@/shared/types/CookieConsentProps'
import {
  readCookiePreferences,
  saveCookiePreferences,
  subscribeToCookiePreferencesOpen
} from '@/shared/utils/cookiePreferences'

export function CookieConsent({ analyticsEnabled }: CookieConsentProps) {
  const [value, setValue] = useState(readCookiePreferences)
  const [open, setOpen] = useState(() => value === null)

  useEffect(() => subscribeToCookiePreferencesOpen(() => setOpen(true)), [])

  function choose(functional: boolean, analytics: boolean) {
    setValue(saveCookiePreferences(functional, analytics))
    setOpen(false)
  }

  function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    choose(formData.has('functional'), analyticsEnabled && formData.has('analytics'))
  }

  if (!open) return null

  return (
    <section
      className="fixed inset-x-0 bottom-0 z-[10001] max-h-[100dvh] overflow-y-auto border-t border-slate-200 bg-white shadow-[0_-12px_32px_rgba(0,0,0,0.16)]"
      aria-labelledby="cookie-settings-title"
    >
      <div className="relative mx-auto grid max-w-5xl items-center gap-4 px-4 py-4 md:grid-cols-[minmax(0,1fr)_auto] md:gap-x-8 md:px-6">
        <div className="pr-8">
          <h2 id="cookie-settings-title" className="text-base font-bold text-slate-950">
            Cookie settings
          </h2>
          <p className="mt-1 max-w-[75ch] text-sm leading-5 text-slate-600">
            Necessary storage keeps enrollment secure. Optional services provide address suggestions
            and help us understand portal usage.
          </p>
          <a
            className="mt-1 inline-flex min-h-11 items-center text-sm font-bold text-[#a63b00] underline decoration-1 underline-offset-4 hover:text-[#7d2d00] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05]"
            href="https://www.160drivingacademy.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            Privacy policy
          </a>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            className="min-h-11 bg-[#d84a05] px-4 text-sm font-bold text-white transition-colors hover:bg-[#b83d03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05] focus-visible:ring-offset-2"
            type="button"
            onClick={() => choose(true, analyticsEnabled)}
          >
            Accept all
          </button>
          <button
            className="min-h-11 border border-slate-300 bg-white px-4 text-sm font-bold text-slate-900 transition-colors hover:border-[#d84a05] hover:text-[#a63b00] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05] focus-visible:ring-offset-2"
            type="button"
            onClick={() => choose(false, false)}
          >
            Reject optional
          </button>
        </div>
        <details className="border-t border-slate-200 pt-2 md:col-span-2">
          <summary className="inline-flex min-h-11 cursor-pointer items-center text-sm font-bold text-[#a63b00] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05]">
            Manage preferences
          </summary>
          <form className="grid gap-3 pb-1 md:grid-cols-2" onSubmit={save}>
            <div className="flex min-h-20 items-center justify-between gap-5 border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <span className="grid gap-1">
                <strong className="text-slate-950">Strictly necessary</strong>
                <small className="text-xs leading-5 text-slate-600">
                  Security, session management, and consent storage.
                </small>
              </span>
              <span className="shrink-0 font-bold text-emerald-700">Always on</span>
            </div>
            <label className="flex min-h-20 items-center justify-between gap-5 border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <span className="grid gap-1">
                <strong className="text-slate-950">Address suggestions</strong>
                <small className="text-xs leading-5 text-slate-600">
                  Loads Google Places while you enter an address.
                </small>
              </span>
              <input
                className="h-5 w-5 shrink-0 accent-[#d84a05]"
                name="functional"
                type="checkbox"
                defaultChecked={value?.functional ?? false}
              />
            </label>
            {analyticsEnabled && (
              <label className="flex min-h-20 items-center justify-between gap-5 border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:col-span-2">
                <span className="grid gap-1">
                  <strong className="text-slate-950">Analytics</strong>
                  <small className="text-xs leading-5 text-slate-600">
                    Helps us improve enrollment by measuring portal usage.
                  </small>
                </span>
                <input
                  className="h-5 w-5 shrink-0 accent-[#d84a05]"
                  name="analytics"
                  type="checkbox"
                  defaultChecked={value?.analytics ?? false}
                />
              </label>
            )}
            <button
              className="min-h-11 bg-[#d84a05] px-4 text-sm font-bold text-white transition-colors hover:bg-[#b83d03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05] focus-visible:ring-offset-2 md:col-start-2"
              type="submit"
            >
              Save preferences
            </button>
          </form>
        </details>
        {value && (
          <button
            className="absolute right-3 top-3 grid h-11 w-11 place-items-center text-slate-600 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d84a05]"
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close cookie settings"
          >
            <X aria-hidden="true" size={20} />
          </button>
        )}
      </div>
    </section>
  )
}
